import { Controller } from "@hotwired/stimulus"

const MAX = 5

export default class extends Controller {
  static targets = ["input", "grid", "counter", "modal", "modalImage", "addZone", "existingInputs"]

  files = []

  connect() {
    // Pre-populate with existing URLs (from server-rendered hidden inputs)
    if (this.hasExistingInputsTarget) {
      const inputs = this.existingInputsTarget.querySelectorAll('input[type="hidden"]')
      inputs.forEach(input => {
        this.files.push({
          _objectUrl: input.dataset.url,
          _isExisting: true,
          _url: input.dataset.url,
          name: input.dataset.url.split("/").pop()
        })
      })
    }
    this._render()

    this._form = this.element.closest("form")
    this._onFormSubmitCapture = () => this._syncInput()
    this._form?.addEventListener("submit", this._onFormSubmitCapture, true)
  }

  disconnect() {
    this._form?.removeEventListener("submit", this._onFormSubmitCapture, true)
    this.files.forEach(f => { if (f._objectUrl && !f._isExisting) URL.revokeObjectURL(f._objectUrl) })
  }

  pick(event) {
    event.preventDefault()
    this.inputTarget.click()
  }

  addFiles(event) {
    const incoming = Array.from(event.currentTarget.files || []).filter(f => f.type.startsWith("image/"))
    const remaining = MAX - this.files.length
    incoming.slice(0, remaining).forEach(f => this.files.push(f))
    this._syncInput()
    this._render()
    event.currentTarget.value = ""
  }

  remove(event) {
    event.stopPropagation()
    const idx = parseInt(event.currentTarget.dataset.index)
    const file = this.files[idx]

    if (file._isExisting) {
      // Remove the corresponding hidden input so it's not submitted
      if (this.hasExistingInputsTarget) {
        const input = this.existingInputsTarget.querySelector(`input[data-url="${CSS.escape(file._url)}"]`) ||
                      Array.from(this.existingInputsTarget.querySelectorAll('input[type="hidden"]'))
                           .find(i => i.dataset.url === file._url)
        if (input) input.remove()
      }
    } else if (file._objectUrl) {
      URL.revokeObjectURL(file._objectUrl)
    }

    this.files.splice(idx, 1)
    this._syncInput()
    this._render()
  }

  openModal(event) {
    const idx = parseInt(event.currentTarget.dataset.index)
    const file = this.files[idx]
    if (!file._objectUrl && !file._isExisting) file._objectUrl = URL.createObjectURL(file)
    this.modalImageTarget.src = file._objectUrl
    this.modalTarget.style.display = "flex"
    document.body.style.overflow = "hidden"
  }

  closeModal(event) {
    if (event && event.target !== this.modalTarget) return
    this._hideModal()
  }

  closeModalBtn() {
    this._hideModal()
  }

  closeModalKey(event) {
    if (event.key === "Escape") this._hideModal()
  }

  _hideModal() {
    this.modalTarget.style.display = "none"
    this.modalImageTarget.src = ""
    document.body.style.overflow = ""
  }

  // ── Private ────────────────────────────────────────────────────────────────

  _render() {
    const count = this.files.length
    const grid  = this.gridTarget

    // Counter
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${count} / ${MAX}`
      this.counterTarget.style.color = count >= MAX ? "var(--ls-accent)" : "rgba(15,23,42,0.4)"
    }

    // Thumbnails
    grid.innerHTML = ""
    this.files.forEach((file, idx) => {
      if (!file._objectUrl && !file._isExisting) file._objectUrl = URL.createObjectURL(file)

      const wrap = document.createElement("div")
      wrap.style.cssText = "position: relative; flex-shrink: 0;"

      const img = document.createElement("img")
      img.src     = file._objectUrl
      img.alt     = file.name
      img.title   = file.name
      img.style.cssText = "max-width: 200px; max-height: 140px; width: auto; height: auto; border-radius: 10px; border: 1.5px solid rgba(148,163,184,0.3); cursor: zoom-in; display: block; transition: opacity 0.15s;"
      img.dataset.index  = idx
      img.dataset.action = "click->screenshot-uploader#openModal"

      const badge = document.createElement("span")
      badge.textContent   = `${idx + 1}/${MAX}`
      badge.style.cssText = "position: absolute; bottom: 5px; left: 5px; font-size: 9px; font-weight: 700; color: #fff; background: rgba(0,0,0,0.55); padding: 1px 6px; border-radius: 20px; pointer-events: none; letter-spacing: 0.03em;"

      const rmBtn = document.createElement("button")
      rmBtn.type           = "button"
      rmBtn.innerHTML      = "×"
      rmBtn.style.cssText  = "position: absolute; top: -7px; right: -7px; width: 20px; height: 20px; border-radius: 50%; background: #0f172a; border: 1.5px solid #fff; color: #fff; font-size: 14px; line-height: 1; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0; box-shadow: 0 1px 4px rgba(0,0,0,0.2);"
      rmBtn.dataset.index  = idx
      rmBtn.dataset.action = "click->screenshot-uploader#remove"

      wrap.appendChild(img)
      wrap.appendChild(badge)
      wrap.appendChild(rmBtn)
      grid.appendChild(wrap)
    })

    // Add zone visibility
    if (this.hasAddZoneTarget) {
      this.addZoneTarget.style.display = count >= MAX ? "none" : "flex"
    }
  }

  _syncInput() {
    try {
      const dt = new DataTransfer()
      this.files.filter(f => !f._isExisting).forEach(f => dt.items.add(f))
      this.inputTarget.files = dt.files
    } catch (_) {
      // DataTransfer not supported
    }
  }
}
